import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { fetchEventSource } from '@microsoft/fetch-event-source';
import axios from 'axios';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { HttpHeaders } from '~/constants';
import { ErrorResponse } from '~/infra/ErrorResponse';
import { selectors } from './state-selectors';
export var APIHost = /*#__PURE__*/ function(APIHost) {
    APIHost["AI"] = "ai";
    APIHost["KYB"] = "kyb";
    APIHost["Plaid"] = "plaid";
    APIHost["Scout"] = "scout";
    APIHost["Mgmt"] = "mgmt";
    APIHost["Cato"] = "cato";
    APIHost["External"] = "external";
    /** @deprecated */ APIHost["Old"] = "old";
    return APIHost;
}({});
const URL_BUSINESS_ID_REGEX = /\/businesses?\/(business-[a-zA-Z0-9_-]+)(?:\/|$|\?)/;
function isUrlBusinessIdStale(url, currentClaimsBusinessId) {
    if (currentClaimsBusinessId == null) return false;
    const match = url.match(URL_BUSINESS_ID_REGEX);
    const urlBusinessId = match === null || match === void 0 ? void 0 : match[1];
    if (urlBusinessId == null) return false;
    return urlBusinessId !== currentClaimsBusinessId;
}
function buildRequestInputs(options, state, getCookie) {
    const { apiHost, url, disableAuth, forceAuthorizationHeader, authToken } = options;
    const { apiHost: apiHostname, apiGatewayHost, useAuthorizationHeader } = state.session;
    const apiGatewayHosts = [
        "plaid",
        "scout",
        "cato",
        "kyb",
        "external",
        "ai",
        "mgmt"
    ];
    const actualHost = apiGatewayHosts.includes(apiHost) ? apiGatewayHost : apiHostname;
    const fullUrl = actualHost + url;
    const prevCookie = getCookie();
    const headers = {};
    headers[HttpHeaders.ContentType] = 'application/json';
    const apiHostHeaderHosts = [
        "plaid",
        "scout",
        "kyb",
        "cato",
        "ai",
        "mgmt"
    ];
    if (apiHostHeaderHosts.includes(apiHost)) {
        headers['x-api-host'] = apiHost;
    }
    const actualAuthToken = (disableAuth || !useAuthorizationHeader) && !forceAuthorizationHeader ? null : authToken !== null && authToken !== void 0 ? authToken : state.authToken;
    if (actualAuthToken) {
        headers.Authorization = "Bearer ".concat(actualAuthToken);
    }
    if (window._sessionId) {
        headers[HttpHeaders.SessionId] = window._sessionId;
    }
    const requestId = generateUniqueId('request');
    headers[HttpHeaders.RequestId] = requestId;
    return {
        fullUrl,
        headers,
        requestId,
        prevCookie
    };
}
export function initSendRequest(param) {
    let { getState, getCookie, onCookieChange, onLogout, onNoAccess, onReportToSentry, onAuthTokenInResponse, onAuthTokenRevoked } = param;
    const sendRequest = function sendRequest(options) {
        var _config_headers;
        const { method, params = {}, data = {}, apiHost, disableAuth, forceAuthorizationHeader, authToken, url, disableSentry, urlForSentry, config, disable403Handling } = options;
        const { fullUrl, headers, requestId, prevCookie } = buildRequestInputs({
            apiHost,
            url,
            disableAuth,
            forceAuthorizationHeader,
            authToken
        }, getState(), getCookie);
        const mergedHeaders = _object_spread({}, config === null || config === void 0 ? void 0 : config.headers, headers);
        if (config === null || config === void 0 ? void 0 : (_config_headers = config.headers) === null || _config_headers === void 0 ? void 0 : _config_headers[HttpHeaders.ContentType]) {
            mergedHeaders[HttpHeaders.ContentType] = config.headers[HttpHeaders.ContentType];
        }
        const axiosConfig = _object_spread_props(_object_spread({}, config), {
            withCredentials: true,
            params,
            headers: mergedHeaders
        });
        const request = axios.request(_object_spread_props(_object_spread({
            method,
            url: fullUrl
        }, axiosConfig), {
            data
        }));
        return request.then((response)=>{
            if (prevCookie !== getCookie()) {
                onCookieChange();
            }
            if (getState().session.useAuthorizationHeader) {
                const body = response.data;
                if (body != null && typeof body === 'object' && typeof body.authToken === 'string' && body.authToken.length > 0) {
                    onAuthTokenInResponse(body.authToken);
                }
            }
            return response;
        }).catch((err)=>{
            var _getState_claims;
            const e = new ErrorResponse(err);
            const is401 = e.status === 401;
            const is403ForReviewer = e.status === 403 && selectors.user.isReviewer(getState());
            const isImpersonating = !!selectors.impersonatingAs(getState());
            if (is401 && apiHost === "mgmt" && getState().session.useAuthorizationHeader) {
                onAuthTokenRevoked();
            }
            if (is401 || is403ForReviewer) onLogout();
            const is403DuringBusinessSwitch = e.status === 403 && getState().isSwitchingBusiness;
            const is403FromStaleBusinessUrl = e.status === 403 && isUrlBusinessIdStale(fullUrl, (_getState_claims = getState().claims) === null || _getState_claims === void 0 ? void 0 : _getState_claims.businessId);
            const skipNoAccessNavigation = is403DuringBusinessSwitch || is403FromStaleBusinessUrl;
            if (!disable403Handling && e.status === 403 && !skipNoAccessNavigation) onNoAccess();
            const disableSentryReporting = isImpersonating || is401 || is403ForReviewer || is403DuringBusinessSwitch || typeof disableSentry === 'function' && disableSentry(e);
            if (!disableSentryReporting && e.status) {
                onReportToSentry({
                    message: "Request (".concat(method.toUpperCase(), " ").concat(urlForSentry || fullUrl, ") failed with status code ").concat(e.status),
                    requestId,
                    method,
                    url: fullUrl,
                    responseBody: e.body ? JSON.stringify(e.body) : ''
                });
            }
            throw err;
        });
    };
    class FatalStreamError extends Error {
        constructor(message, status){
            super(message), _define_property(this, "status", void 0);
            this.name = 'FatalStreamError';
            this.status = status;
        }
    }
    const sendStream = async (options)=>{
        const state = getState();
        const { fullUrl, headers, requestId, prevCookie } = buildRequestInputs({
            apiHost: options.apiHost,
            url: options.url,
            disableAuth: options.disableAuth,
            authToken: undefined,
            forceAuthorizationHeader: false
        }, state, getCookie);
        await fetchEventSource(fullUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(options.data),
            credentials: 'include',
            signal: options.signal,
            openWhenHidden: true,
            onopen: async (res)=>{
                var _res_headers_get;
                if (prevCookie !== getCookie()) onCookieChange();
                if (res.status === 401) {
                    onLogout();
                    throw new FatalStreamError('unauthorized', 401);
                }
                if (res.status === 403) {
                    if (selectors.user.isReviewer(state)) onLogout();
                    else onNoAccess();
                    throw new FatalStreamError('forbidden', 403);
                }
                if (!res.ok) throw new FatalStreamError("stream open failed: ".concat(res.status), res.status);
                const contentType = (_res_headers_get = res.headers.get('content-type')) !== null && _res_headers_get !== void 0 ? _res_headers_get : '';
                if (!contentType.includes('text/event-stream')) {
                    throw new FatalStreamError('expected text/event-stream');
                }
            },
            onmessage: (evt)=>{
                if (evt.event === 'done') return;
                const parsed = options.parseEvent(evt);
                if (parsed == null) return;
                options.onEvent(parsed);
            },
            onerror: (err)=>{
                const isAuthErr = err instanceof FatalStreamError && (err.status === 401 || err.status === 403);
                const isImpersonating = !!selectors.impersonatingAs(state);
                const appErr = err instanceof ErrorResponse ? err : new ErrorResponse({
                    response: {
                        status: err instanceof FatalStreamError ? err.status : undefined,
                        data: undefined
                    }
                });
                const disableByPredicate = typeof options.disableSentry === 'function' && options.disableSentry(appErr);
                const shouldReport = !isAuthErr && !isImpersonating && !disableByPredicate;
                if (shouldReport) {
                    onReportToSentry({
                        message: "Stream (POST ".concat(options.urlForSentry || fullUrl, ") failed: ").concat(err),
                        requestId,
                        method: 'POST',
                        url: fullUrl,
                        responseBody: ''
                    });
                }
                throw err;
            }
        });
    };
    return {
        sendRequest,
        sendStream
    };
}
